/* pi-hero-slideshow.js — Aurora-style fade hero carousel ============== */
(function () {
  if (customElements.get('pi-hero-slideshow')) return;

  const RING_LENGTH = 100;

  class PiHeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.slides = [];
      this.dots = [];
      this.index = 0;
      this.advanceTimer = null;
      this.speed = 6000;
      this.userPaused = false;
      this.autoplayEnabled = true;
      this.transitioning = false;
      this.fadeMs = 600;
      this.headerObserver = null;
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.updateHeaderOffset = this.updateHeaderOffset.bind(this);
    }

    connectedCallback() {
      this.carousel = this.querySelector('.slideshow__carousel');
      if (!this.carousel) return;

      this.sectionEl = this.closest('.pi-slideshow-section');
      this.slides = Array.from(this.carousel.querySelectorAll('.pi-slideshow__slide'));
      this.dots = Array.from(this.querySelectorAll('.pi-slideshow__dot'));
      this.autoplayBtn = this.querySelector('.pi-slideshow__autoplay');
      this.speed = parseInt(this.getAttribute('data-speed'), 10) * 1000 || 6000;
      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';

      const fadeRaw = getComputedStyle(this.sectionEl || document.documentElement).getPropertyValue(
        '--pi-slideshow-fade-duration'
      );
      this.fadeMs = parseInt(String(fadeRaw).replace(/[^\d]/g, ''), 10) || 600;

      this.style.setProperty('--pi-slideshow-autoplay-duration', this.speed + 'ms');

      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.slides.length === 0) return;

      this.dataset.count = String(this.slides.length);
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.dataset.active === 'true')
      );
      if (this.index < 0) this.index = 0;

      this.updateHeaderOffset();
      window.addEventListener('resize', this.onResize);
      this.observeHeader();

      this.syncSlides();
      this.syncDots();
      this.syncPlaybackState();

      const prev = this.querySelector('.pi-slideshow__nav--prev');
      const next = this.querySelector('.pi-slideshow__nav--next');

      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });

      if (this.autoplayBtn) {
        this.autoplayBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleAutoplay();
        });
      }

      if (this.slides.length <= 1 || prefersReduced) {
        this.autoplayEnabled = false;
        this.userPaused = true;
        this.syncPlaybackState();
        return;
      }

      this.addEventListener('keydown', this.onKeyDown);

      if (this.autoplayEnabled) this.scheduleAdvance();
    }

    disconnectedCallback() {
      this.clearAdvance();
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('keydown', this.onKeyDown);
      if (this.headerObserver) this.headerObserver.disconnect();
    }

    onResize() {
      this.updateHeaderOffset();
    }

    observeHeader() {
      const header =
        document.querySelector('.shopify-section-group-header-group') ||
        document.querySelector('pi-sticky-header') ||
        document.querySelector('.pi-header-wrapper');

      if (!header || typeof ResizeObserver === 'undefined') return;

      this.headerObserver = new ResizeObserver(this.updateHeaderOffset);
      this.headerObserver.observe(header);
    }

    updateHeaderOffset() {
      const heightMode = this.sectionEl?.dataset.height || 'fullscreen';
      if (heightMode === 'natural') return;

      const header =
        document.querySelector('.shopify-section-group-header-group') ||
        document.querySelector('pi-sticky-header') ||
        document.querySelector('.pi-header-wrapper');

      const height = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--pi-header-offset', height + 'px');

      if (this.sectionEl) {
        const styles = getComputedStyle(this.sectionEl);
        const padBottom = parseFloat(styles.paddingBottom) || 0;
        this.sectionEl.style.setProperty('--pi-slideshow-padding-bottom', padBottom + 'px');
      }
    }

    onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(1);
      }
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.syncPlaybackState();
      if (!this.userPaused && this.autoplayEnabled) {
        this.scheduleAdvance();
      } else {
        this.clearAdvance();
      }
    }

    syncPlaybackState() {
      const playing =
        this.autoplayEnabled && !this.userPaused && this.slides.length > 1;
      this.dataset.playing = String(playing);
      this.dataset.userPaused = String(this.userPaused);

      if (this.autoplayBtn) {
        this.autoplayBtn.setAttribute('data-paused', String(this.userPaused));
        this.autoplayBtn.setAttribute(
          'aria-label',
          this.userPaused
            ? this.autoplayBtn.getAttribute('data-label-play') || 'Play slideshow'
            : this.autoplayBtn.getAttribute('data-label-pause') || 'Pause slideshow'
        );
      }
    }

    scheduleAdvance() {
      this.clearAdvance();
      if (!this.autoplayEnabled || this.userPaused || this.slides.length <= 1) return;

      this.restartProgress();

      this.advanceTimer = window.setTimeout(() => {
        if (!this.userPaused && this.autoplayEnabled && !this.transitioning) this.go(1);
      }, this.speed);
    }

    clearAdvance() {
      if (this.advanceTimer) window.clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }

    restartProgress() {
      const activeDot = this.dots[this.index];
      if (!activeDot) return;

      const border = activeDot.querySelector('.pi-slideshow__dot-border');
      if (!border) return;

      border.style.animation = 'none';
      border.style.strokeDashoffset = String(RING_LENGTH);
      void border.offsetWidth;
      border.style.animation = '';
    }

    syncSlides() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.dataset.active = String(active);
        if (!active && slide.dataset.leaving !== 'true') slide.dataset.leaving = 'false';
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.tabIndex = active ? 0 : -1;
      });
    }

    getWrapDistance(a, b, total) {
      const diff = Math.abs(a - b);
      return Math.min(diff, total - diff);
    }

    syncDotSizes() {
      const total = this.slides.length;
      this.dots.forEach((dot, i) => {
        if (i === this.index) return;

        const dist = this.getWrapDistance(i, this.index, total);
        dot.classList.remove('pi-slideshow__dot--size-medium', 'pi-slideshow__dot--size-small');

        if (dist === 1) {
          dot.classList.add('pi-slideshow__dot--size-medium');
        } else {
          dot.classList.add('pi-slideshow__dot--size-small');
        }
      });
    }

    syncDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('pi-slideshow__dot--active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        dot.classList.remove('pi-slideshow__dot--size-medium', 'pi-slideshow__dot--size-small');
      });
      this.syncDotSizes();
      this.syncPlaybackState();
    }

    goTo(targetIndex) {
      if (this.transitioning || targetIndex === this.index || this.slides.length <= 1) return;
      if (targetIndex < 0 || targetIndex >= this.slides.length) return;
      this.transitionTo(targetIndex);
    }

    go(direction) {
      if (this.transitioning || this.slides.length <= 1) return;
      const nextIndex = (this.index + direction + this.slides.length) % this.slides.length;
      this.transitionTo(nextIndex);
    }

    transitionTo(nextIndex) {
      if (nextIndex === this.index || this.slides.length <= 1) return;

      const current = this.slides[this.index];
      const next = this.slides[nextIndex];

      this.clearAdvance();
      this.transitioning = true;
      current.dataset.leaving = 'true';
      current.dataset.active = 'false';
      next.dataset.active = 'true';
      next.dataset.leaving = 'false';

      this.index = nextIndex;
      this.syncDots();

      window.setTimeout(() => {
        current.dataset.leaving = 'false';
        this.syncSlides();
        this.transitioning = false;
        if (this.autoplayEnabled && !this.userPaused) this.scheduleAdvance();
      }, this.fadeMs);
    }
  }

  customElements.define('pi-hero-slideshow', PiHeroSlideshow);
})();
